import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiMail, FiXCircle } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../services/api";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const mode = searchParams.get("mode") || "";
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      setIsLoading(true);
      setStatus("pending");
      setMessage("");
      try {
        const response = await api.get(mode === "change-email" ? "/change-email/verify" : "/verify-email", {
          params: { token },
        });
        if (response.data?.status !== "success") {
          setStatus("error");
          setMessage(response.data?.message || "Verification failed.");
          return;
        }
        setStatus("success");
        setMessage(
          mode === "change-email"
            ? "Your email has been updated. You can return to your profile."
            : "Your email has been verified. You can now log in."
        );
      } catch (error) {
        const msg = error?.response?.data?.message || "Verification failed.";
        setStatus("error");
        setMessage(msg);
      } finally {
        setIsLoading(false);
      }
    };

    if (!token) {
      setIsLoading(false);
      setStatus("error");
      setMessage("Verification token is missing.");
      return;
    }

    verifyEmail();
  }, [mode, token]);

  useEffect(() => {
    if (status !== "success") {
      return;
    }
    const timer = setTimeout(() => {
      navigate(mode === "change-email" ? "/profile" : "/login");
    }, 10000);
    return () => clearTimeout(timer);
  }, [mode, navigate, status]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-[linear-gradient(135deg,#e0f2fe_0%,#fce7f3_55%,#fed7aa_100%)] px-6 py-10"
    >
      <div className="mx-auto flex w-full max-w-[900px] flex-col gap-6">
        <header className="flex items-center gap-3 rounded-3xl border border-white/60 bg-white/90 px-6 py-4 shadow-lg shadow-slate-200/60">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--brand-blue)_0%,var(--brand-blue-2)_100%)] text-white">
            <FiMail />
          </div>
          <div className="text-lg font-semibold text-slate-900">
            RMC2 Emergency Care (ER)
          </div>
          <Link
            to="/login"
            className="ml-auto text-sm font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-blue-2)]"
          >
            Back to Login
          </Link>
        </header>

        <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-lg shadow-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              {status === "success" ? <FiCheckCircle /> : <FiXCircle />}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Verification
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                {status === "success" ? "Email verified" : "Verification status"}
              </h2>
            </div>
          </div>

          {isLoading ? (
            <p className="mt-6 text-sm text-slate-500">Verifying your email...</p>
          ) : (
            <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {message || "-"}
            </div>
          )}

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(mode === "change-email" ? "/profile" : "/login")}
              className="rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
            >
              {mode === "change-email" ? "Go to Profile" : "Go to Login"}
            </button>
          </div>

          {status === "success" && (
            <p className="mt-4 text-xs text-slate-400">
              You will be redirected in 10 seconds.
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default VerifyEmail;
